import { isSupabaseConfigured, supabase } from '@/lib/supabase';

export type HeaderStyle = 'auto' | 'light' | 'dark';
export type TaxDisplay = 'exclusive' | 'inclusive' | 'hidden';
export type PaymentMode = 'test' | 'live';
export type GatewayCode =
  | '2checkout'
  | 'stripe'
  | 'paypal'
  | 'paddle'
  | 'sslcommerz'
  | 'manual'
  | 'crypto';

export interface SiteLink {
  id: string;
  label: string;
  href: string;
  enabled: boolean;
}

export interface DashboardMenuItem extends SiteLink {
  icon: string;
}

export interface SocialLink {
  id: string;
  label: string;
  href: string;
  icon: string;
  enabled: boolean;
}

export interface SiteSettings {
  general: {
    storeName: string;
    shortName: string;
    logoUrl: string;
    dashboardLogoUrl: string;
    faviconUrl: string;
    supportEmail: string;
    supportUrl: string;
    supportResponseTime: string;
    defaultCurrency: string;
    notFoundUrl: string;
  };
  header: {
    brandText: string;
    logoUrl: string;
    style: HeaderStyle;
    sticky: boolean;
    menuItems: SiteLink[];
    loggedOutPrimaryLabel: string;
    loggedOutPrimaryHref: string;
    loggedOutSecondaryLabel: string;
    loggedOutSecondaryHref: string;
    profileMenuItems: DashboardMenuItem[];
  };
  footer: {
    logoUrl: string;
    brandName: string;
    description: string;
    productLinks: SiteLink[];
    supportLinks: SiteLink[];
    companyLinks: SiteLink[];
    socialLinks: SocialLink[];
    copyrightText: string;
    contactHref: string;
  };
  dashboard: {
    adminSidebarTitle: string;
    customerSidebarTitle: string;
    adminTopbarTitle: string;
    customerTopbarTitle: string;
    showVisitShop: boolean;
    adminMenu: DashboardMenuItem[];
    customerMenu: DashboardMenuItem[];
    quickActions: DashboardMenuItem[];
  };
  checkout: {
    requireAccount: boolean;
    guestCheckout: boolean;
    showPricingOnCards: boolean;
    successUrl: string;
    failedPaymentUrl: string;
    taxDisplay: TaxDisplay;
  };
  payments: {
    mode: PaymentMode;
    enabledGateways: GatewayCode[];
    defaultGateway: GatewayCode;
    webhookUrl: string;
    twoCheckoutMerchantCode: string;
    manualContactLink: string;
    stripe: {
      publishableKey: string;
      secretKey: string;
      webhookSecret: string;
    };
    nowpayments: {
      enabled: boolean;
      apiKey: string;
      publicKey: string;
      ipnSecret: string;
      ipnUrl: string;
      acceptedCurrencies: string;
      defaultPayCurrency: string;
      statusPaid: string;
      statusFailed: string;
      payoutWallet: string;
      payoutCurrency: string;
    };
  };
  notifications: {
    adminEnabled: boolean;
    customerEnabled: boolean;
    newOrder: boolean;
    licenseActivation: boolean;
    webhookFailure: boolean;
  };
  support: {
    autoReplyEnabled: boolean;
    autoReplyMessage: string;
    emailNotifications: boolean;
    emailDelayMinutes: number;
    theme: string;
    resendApiKey: string;
    businessHoursEnabled: boolean;
    businessHoursStart: string;
    businessHoursEnd: string;
    businessTimeZone: string;
    readReceipts: boolean;
    maxMessageLength: number;
    awayMessage: string;
  };
}

const DEFAULT_LOGO_URL =
  'https://public.readdy.ai/ai/img_res/4df53d9e-ba4b-4694-9f59-a23ba95942ed.png';
const STORAGE_KEY = 'motionsdio.site-settings';
const SETTINGS_ID = 'default';

const adminMenu: DashboardMenuItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    href: '/admin',
    icon: 'ri-dashboard-line',
    enabled: true,
  },
  {
    id: 'products',
    label: 'Products',
    href: '/admin/products',
    icon: 'ri-box-3-line',
    enabled: true,
  },
  {
    id: 'plans',
    label: 'Plans',
    href: '/admin/plans',
    icon: 'ri-price-tag-3-line',
    enabled: true,
  },
  {
    id: 'categories',
    label: 'Categories',
    href: '/admin/categories',
    icon: 'ri-folder-3-line',
    enabled: true,
  },
  {
    id: 'types',
    label: 'Types',
    href: '/admin/types',
    icon: 'ri-list-check-3',
    enabled: true,
  },
  {
    id: 'media',
    label: 'Media',
    href: '/admin/media',
    icon: 'ri-image-line',
    enabled: true,
  },
  {
    id: 'orders',
    label: 'Orders',
    href: '/admin/orders',
    icon: 'ri-shopping-bag-3-line',
    enabled: true,
  },
  {
    id: 'licenses',
    label: 'Licenses',
    href: '/admin/licenses',
    icon: 'ri-key-2-line',
    enabled: true,
  },
  {
    id: 'customers',
    label: 'Customers',
    href: '/admin/customers',
    icon: 'ri-team-line',
    enabled: true,
  },
  {
    id: 'activations',
    label: 'Activations',
    href: '/admin/activations',
    icon: 'ri-global-line',
    enabled: true,
  },
  {
    id: 'downloads',
    label: 'Downloads',
    href: '/admin/downloads',
    icon: 'ri-download-cloud-line',
    enabled: true,
  },
  {
    id: 'reviews',
    label: 'Reviews',
    href: '/admin/reviews',
    icon: 'ri-star-line',
    enabled: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    href: '/admin/settings',
    icon: 'ri-settings-4-line',
    enabled: true,
  },
  {
    id: 'support',
    label: 'Support Center',
    href: '/admin/support',
    icon: 'ri-customer-service-2-line',
    enabled: true,
  },
  {
    id: 'activity',
    label: 'Activity Log',
    href: '/admin/activity',
    icon: 'ri-history-line',
    enabled: true,
  },
  {
    id: 'webhooks',
    label: 'Webhook Logs',
    href: '/admin/webhooks',
    icon: 'ri-bug-line',
    enabled: true,
  },
];

const customerMenu: DashboardMenuItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    href: '/dashboard',
    icon: 'ri-dashboard-line',
    enabled: true,
  },
  {
    id: 'orders',
    label: 'Orders',
    href: '/dashboard/orders',
    icon: 'ri-shopping-bag-3-line',
    enabled: true,
  },
  {
    id: 'licenses',
    label: 'Licenses',
    href: '/dashboard/licenses',
    icon: 'ri-key-2-line',
    enabled: true,
  },
  {
    id: 'downloads',
    label: 'Downloads',
    href: '/dashboard/downloads',
    icon: 'ri-download-cloud-line',
    enabled: true,
  },
  {
    id: 'profile',
    label: 'Profile',
    href: '/dashboard/profile',
    icon: 'ri-user-settings-line',
    enabled: true,
  },
];

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  general: {
    storeName: 'Motionsdio Store',
    shortName: 'Motionsdio',
    logoUrl: DEFAULT_LOGO_URL,
    dashboardLogoUrl: DEFAULT_LOGO_URL,
    faviconUrl: '',
    supportEmail: 'hello@motionsdio.com',
    supportUrl: 'https://motionsdio.com/support',
    supportResponseTime: '< 4 hours avg',
    defaultCurrency: 'USD',
    notFoundUrl: '/products',
  },
  header: {
    brandText: 'Motionsdio',
    logoUrl: DEFAULT_LOGO_URL,
    style: 'auto',
    sticky: true,
    menuItems: [
      { id: 'products', label: 'Products', href: '/products', enabled: true },
      { id: 'pricing', label: 'Pricing', href: '/pricing', enabled: true },
      { id: 'docs', label: 'Docs', href: '/docs', enabled: true },
    ],
    loggedOutPrimaryLabel: 'Get Started',
    loggedOutPrimaryHref: '/register',
    loggedOutSecondaryLabel: 'Log in',
    loggedOutSecondaryHref: '/login',
    profileMenuItems: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        href: '{dashboard}',
        icon: 'ri-dashboard-line',
        enabled: true,
      },
      {
        id: 'profile',
        label: 'Profile',
        href: '{dashboard}/profile',
        icon: 'ri-user-settings-line',
        enabled: true,
      },
      {
        id: 'orders',
        label: 'Orders',
        href: '{dashboard}/orders',
        icon: 'ri-shopping-bag-3-line',
        enabled: true,
      },
      {
        id: 'downloads',
        label: 'Downloads',
        href: '{dashboard}/downloads',
        icon: 'ri-download-cloud-line',
        enabled: true,
      },
    ],
  },
  footer: {
    logoUrl: DEFAULT_LOGO_URL,
    brandName: 'Motionsdio',
    description:
      'Premium WordPress plugins, themes, and templates trusted by thousands of agencies and developers worldwide.',
    productLinks: [
      {
        id: 'plugins',
        label: 'Plugins',
        href: '/products?type=plugins',
        enabled: true,
      },
      {
        id: 'themes',
        label: 'Themes',
        href: '/products?type=themes',
        enabled: true,
      },
      {
        id: 'templates',
        label: 'Templates',
        href: '/products?type=templates',
        enabled: true,
      },
      {
        id: 'addons',
        label: 'Addons',
        href: '/products?type=addons',
        enabled: true,
      },
      {
        id: 'assets',
        label: 'Assets',
        href: '/products?type=assets',
        enabled: true,
      },
    ],
    supportLinks: [
      { id: 'docs', label: 'Documentation', href: '/docs', enabled: true },
      { id: 'changelog', label: 'Changelog', href: '/products', enabled: true },
      { id: 'faq', label: 'License FAQ', href: '/pricing', enabled: true },
      {
        id: 'contact',
        label: 'Contact',
        href: 'https://motionsdio.com/support',
        enabled: true,
      },
    ],
    companyLinks: [
      { id: 'about', label: 'About', href: '/about', enabled: false },
      { id: 'blog', label: 'Blog', href: '/blog', enabled: false },
      { id: 'careers', label: 'Careers', href: '/careers', enabled: false },
      { id: 'privacy', label: 'Privacy', href: '/privacy', enabled: false },
      { id: 'terms', label: 'Terms', href: '/terms', enabled: false },
    ],
    socialLinks: [
      {
        id: 'twitter',
        label: 'Twitter',
        href: '#',
        icon: 'ri-twitter-fill',
        enabled: true,
      },
      {
        id: 'github',
        label: 'GitHub',
        href: '#',
        icon: 'ri-github-fill',
        enabled: true,
      },
      {
        id: 'youtube',
        label: 'YouTube',
        href: '#',
        icon: 'ri-youtube-fill',
        enabled: true,
      },
    ],
    copyrightText: 'Motionsdio Store. All rights reserved.',
    contactHref: 'https://motionsdio.com/support',
  },
  dashboard: {
    adminSidebarTitle: 'Admin',
    customerSidebarTitle: 'Dashboard',
    adminTopbarTitle: 'Admin Panel',
    customerTopbarTitle: 'Customer Dashboard',
    showVisitShop: true,
    adminMenu,
    customerMenu,
    quickActions: [
      {
        id: 'browse-store',
        label: 'Browse Store',
        href: '/products',
        icon: 'ri-store-2-line',
        enabled: true,
      },
      {
        id: 'view-licenses',
        label: 'View Licenses',
        href: '/dashboard/licenses',
        icon: 'ri-key-2-line',
        enabled: true,
      },
      {
        id: 'download-products',
        label: 'Download Products',
        href: '/dashboard/downloads',
        icon: 'ri-download-cloud-line',
        enabled: true,
      },
      {
        id: 'update-profile',
        label: 'Update Profile',
        href: '/dashboard/profile',
        icon: 'ri-user-settings-line',
        enabled: true,
      },
    ],
  },
  checkout: {
    requireAccount: true,
    guestCheckout: false,
    showPricingOnCards: true,
    successUrl: '/dashboard/orders',
    failedPaymentUrl: '/pricing',
    taxDisplay: 'exclusive',
  },
  payments: {
    mode: 'test',
    enabledGateways: ['2checkout'],
    defaultGateway: '2checkout',
    webhookUrl: 'https://store.motionsdio.com/api/webhooks/2checkout',
    twoCheckoutMerchantCode: '',
    manualContactLink: '',
    stripe: {
      publishableKey: '',
      secretKey: '',
      webhookSecret: '',
    },
    nowpayments: {
      enabled: false,
      apiKey: '',
      publicKey: '',
      ipnSecret: '',
      ipnUrl: 'https://osfgjvbfojyogspduast.supabase.co/functions/v1/nowpayments-webhook',
      acceptedCurrencies: 'BTC,ETH,USDT,USDC,BNB,DOGE,SOL,XRP,ADA,MATIC,TRX',
      defaultPayCurrency: 'USDTBSC',
      statusPaid: 'paid',
      statusFailed: 'failed',
      payoutWallet: '',
      payoutCurrency: 'USDTBSC',
    },
  },
  notifications: {
    adminEnabled: true,
    customerEnabled: true,
    newOrder: true,
    licenseActivation: true,
    webhookFailure: true,
  },
  support: {
    autoReplyEnabled: true,
    autoReplyMessage: 'Hi there! Thanks for reaching out. We have received your message and will get back to you shortly.',
    emailNotifications: false,
    emailDelayMinutes: 5,
    theme: 'brand',
    resendApiKey: '',
    businessHoursEnabled: false,
    businessHoursStart: '09:00',
    businessHoursEnd: '17:00',
    businessTimeZone: 'UTC',
    readReceipts: true,
    maxMessageLength: 5000,
    awayMessage: 'We are currently away. We will respond during our business hours.',
  },
};

function cloneDefaults(): SiteSettings {
  return JSON.parse(JSON.stringify(DEFAULT_SITE_SETTINGS)) as SiteSettings;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeSection<T extends Record<string, unknown>>(
  defaults: T,
  value: unknown,
): T {
  return {
    ...defaults,
    ...(isRecord(value) ? value : {}),
  };
}

function mergeArray<T extends { id: string }>(defaults: T[], value: unknown): T[] {
  if (!Array.isArray(value)) return defaults;
  const savedMap = new Map<string, T>((value as T[]).map((item) => [item.id, item]));
  // Keep saved items in saved order, append any new defaults not yet saved
  const seen = new Set<string>();
  const merged: T[] = [];
  for (const item of value as T[]) {
    merged.push(item);
    seen.add(item.id);
  }
  for (const item of defaults) {
    if (!seen.has(item.id)) {
      merged.push(item);
    }
  }
  return merged;
}

export function mergeSiteSettings(value: unknown): SiteSettings {
  const defaults = cloneDefaults();
  const input = isRecord(value) ? value : {};
  const settings: SiteSettings = {
    general: mergeSection(defaults.general, input.general),
    header: mergeSection(defaults.header, input.header),
    footer: mergeSection(defaults.footer, input.footer),
    dashboard: mergeSection(defaults.dashboard, input.dashboard),
    checkout: mergeSection(defaults.checkout, input.checkout),
    payments: mergeSection(defaults.payments, input.payments),
    notifications: mergeSection(defaults.notifications, input.notifications),
    support: mergeSection(defaults.support, input.support),
  };

  settings.header.menuItems = mergeArray(
    defaults.header.menuItems,
    settings.header.menuItems,
  );
  settings.header.profileMenuItems = mergeArray(
    defaults.header.profileMenuItems,
    settings.header.profileMenuItems,
  );
  settings.footer.productLinks = mergeArray(
    defaults.footer.productLinks,
    settings.footer.productLinks,
  );
  settings.footer.supportLinks = mergeArray(
    defaults.footer.supportLinks,
    settings.footer.supportLinks,
  );
  settings.footer.companyLinks = mergeArray(
    defaults.footer.companyLinks,
    settings.footer.companyLinks,
  );
  settings.footer.socialLinks = mergeArray(
    defaults.footer.socialLinks,
    settings.footer.socialLinks,
  );
  settings.dashboard.adminMenu = mergeArray(
    defaults.dashboard.adminMenu,
    settings.dashboard.adminMenu,
  );
  settings.dashboard.customerMenu = mergeArray(
    defaults.dashboard.customerMenu,
    settings.dashboard.customerMenu,
  );
  settings.dashboard.quickActions = mergeArray(
    defaults.dashboard.quickActions,
    settings.dashboard.quickActions,
  );

  return settings;
}

function readLocalSettings(): SiteSettings {
  if (typeof window === 'undefined') return cloneDefaults();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return mergeSiteSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return cloneDefaults();
  }
}

function writeLocalSettings(settings: SiteSettings) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export async function loadSiteSettings(): Promise<SiteSettings> {
  if (isSupabaseConfigured && supabase) {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('settings')
        .eq('id', SETTINGS_ID)
        .maybeSingle();

      if (!error && data?.settings) {
        const settings = mergeSiteSettings(data.settings);
        writeLocalSettings(settings);
        return settings;
      }
    } catch {
      // Fall back to local settings/defaults if the migration has not been applied yet.
    }
  }

  return readLocalSettings();
}

export async function saveSiteSettings(
  nextSettings: SiteSettings,
): Promise<SiteSettings> {
  const settings = mergeSiteSettings(nextSettings);
  writeLocalSettings(settings);

  if (isSupabaseConfigured && supabase) {
    const { error } = await supabase.from('site_settings').upsert({
      id: SETTINGS_ID,
      settings,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      throw error;
    }
  }

  return settings;
}

export function resolveDashboardHref(href: string, dashboardPath: string) {
  return href.replace('{dashboard}', dashboardPath);
}

export function isExternalHref(href: string) {
  return (
    /^https?:\/\//i.test(href) ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:')
  );
}
